import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Loader } from '../components/Loader'
import { productsApi } from '../services/api'
import { Product, TYPE_LABELS, CATEGORY_LABELS, MASKED_PRICE, OrderType } from '../types'
import { formatToman, toFaDigits, formatDateFa } from '../utils/format'
import { useCart } from '../context/CartContext'
import { useTrading } from '../context/TradingContext'
import toast from 'react-hot-toast'

/** پنل ثبت سفارش برای یک سمت معامله (خرید از ما یا فروش به ما) */
const OrderPanel: React.FC<{
  mode: OrderType
  product: Product
  hidden: boolean
  blocked: boolean
  blockedMessage: string
}> = ({ mode, product, hidden, blocked, blockedMessage }) => {
  const { addItem } = useCart()
  const [qty, setQty] = useState(1)

  const isBuy = mode === 'BUY'
  const unitPrice = isBuy ? product.sellingPrice : product.buybackPrice
  const total = (unitPrice ?? 0) * qty

  const accent = isBuy
    ? { border: 'border-primary-200', header: 'bg-primary-600', text: 'text-primary-700', box: 'bg-primary-50 border-primary-100' }
    : { border: 'border-gold-300', header: 'bg-gold-600', text: 'text-gold-800', box: 'bg-gold-50 border-gold-200' }

  const submit = () => {
    if (blocked) {
      toast.error(blockedMessage)
      return
    }
    addItem(product, qty, mode)
  }

  return (
    <div className={`card p-6 h-fit border-2 ${accent.border}`}>
      <div className={`${accent.header} text-white rounded-lg px-4 py-3 mb-5 flex items-center justify-between`}>
        <h2 className="font-black">{isBuy ? 'سفارش خرید (از ما)' : 'سفارش فروش (به ما)'}</h2>
        <span className="text-xs bg-white/20 rounded-full px-2 py-0.5">
          {isBuy ? 'ما به شما می‌فروشیم' : 'ما از شما می‌خریم'}
        </span>
      </div>

      {blocked ? (
        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 text-sm text-red-700 leading-6 mb-5">
          <b>⛔ این بخش موقتاً بسته است.</b>
          <br />
          {blockedMessage}
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm text-gray-600">قیمت هر واحد</span>
            <span className={`text-lg font-black ${accent.text}`}>
              {hidden ? MASKED_PRICE : formatToman(unitPrice!)}
            </span>
          </div>

          <div className="flex items-center gap-3 mb-5">
            <span className="text-sm text-gray-600">تعداد:</span>
            <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
              <button className="px-4 py-2 text-lg font-bold hover:bg-gray-100" onClick={() => setQty(Math.max(1, qty - 1))}>−</button>
              <span className="px-6 py-2 border-x border-gray-200 font-bold">{toFaDigits(qty)}</span>
              <button className="px-4 py-2 text-lg font-bold hover:bg-gray-100" onClick={() => setQty(qty + 1)}>+</button>
            </div>
          </div>

          <div className={`${accent.box} border rounded-lg p-4 mb-5 text-sm`}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-gray-700">{isBuy ? 'جمع قابل پرداخت' : 'جمع دریافتی شما'}</span>
              <span className={`font-black ${accent.text}`}>
                {hidden ? MASKED_PRICE : formatToman(total)}
              </span>
            </div>
            <div className="flex items-center justify-between text-xs text-gray-600">
              <span>وزن کل</span>
              <span className="font-bold">{toFaDigits(product.weight * qty)} گرم</span>
            </div>
          </div>
        </>
      )}

      <button
        className={`w-full !py-3.5 ${
          blocked
            ? 'btn bg-gray-100 text-gray-400 cursor-not-allowed'
            : isBuy
              ? 'btn btn-primary'
              : 'btn !bg-gold-600 !text-white hover:!bg-gold-700'
        }`}
        onClick={submit}
        disabled={blocked}
      >
        {blocked ? 'بسته شده' : isBuy ? 'افزودن به سبد خرید' : 'افزودن به سبد فروش'}
      </button>

      <p className="text-[11px] text-gray-500 leading-5 mt-3">
        {isBuy
          ? 'قیمت فروش = قیمت خام × (۱ + درصد سود مجموعه)'
          : 'قیمت خرید از شما = قیمت خام × (۱ + درصد منفی خرید که مدیر تعیین کرده است)'}
      </p>
    </div>
  )
}

const ProductDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>()
  const [product, setProduct] = useState<Product | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const { buyBlocked, sellBlocked, pricesHidden } = useTrading()

  useEffect(() => {
    if (!slug) return
    setLoading(true)
    setError('')
    productsApi
      .getBySlug(slug)
      .then((res) => {
        if (res.data.success && res.data.data) setProduct(res.data.data)
        else setError(res.data.message || 'محصول یافت نشد')
      })
      .catch(() => setError('خطا در دریافت اطلاعات محصول'))
      .finally(() => setLoading(false))
  }, [slug])

  if (loading) {
    return (
      <div className="container-main py-20 flex justify-center">
        <Loader />
      </div>
    )
  }

  if (error || !product) {
    return (
      <div className="container-main py-20 text-center">
        <p className="text-gray-700 mb-4">{error || 'محصول یافت نشد'}</p>
        <Link to="/products" className="btn btn-primary">بازگشت به محصولات</Link>
      </div>
    )
  }

  const hidden = pricesHidden || product.priceHidden || product.sellingPrice === null

  return (
    <div className="container-main py-10">
      <nav className="text-xs text-gray-500 mb-6">
        <Link to="/" className="hover:text-primary-600">خانه</Link>
        <span className="mx-1">/</span>
        <Link to="/products" className="hover:text-primary-600">محصولات</Link>
        <span className="mx-1">/</span>
        <span className="text-gray-800">{product.name}</span>
      </nav>

      {/* اطلاعات محصول */}
      <div className="card p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <span className={`badge ${product.type === 'GOLD' ? 'badge-gold' : 'badge-silver'}`}>{TYPE_LABELS[product.type]}</span>
          <span className="badge bg-primary-50 text-primary-700">{CATEGORY_LABELS[product.category]}</span>
          <span className="badge badge-success">فعال</span>
        </div>

        <h1 className="text-2xl font-black text-gray-900 mb-3">{product.name}</h1>
        <p className="text-sm text-gray-600 leading-7 mb-6">{product.description || 'توضیحاتی ثبت نشده است.'}</p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-gray-500 text-xs mb-1">وزن</div>
            <div className="font-bold text-gray-900">{toFaDigits(product.weight)} گرم</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-gray-500 text-xs mb-1">عیار</div>
            <div className="font-bold text-gray-900">{toFaDigits(product.purity)}</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-gray-500 text-xs mb-1">قیمت خام (این عیار)</div>
            <div className="font-bold text-gray-900">
              {hidden ? MASKED_PRICE : formatToman(product.basePrice!)}
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-gray-500 text-xs mb-1">درصد سود مجموعه</div>
            <div className="font-bold text-gray-900">
              {hidden || product.markupPercent === null ? MASKED_PRICE : `${toFaDigits(product.markupPercent)}٪`}
            </div>
          </div>
        </div>
      </div>

      {/* دو پنل جدا کنار هم: سفارش خرید و سفارش فروش */}
      <div className="grid md:grid-cols-2 gap-6">
        <OrderPanel
          mode="BUY"
          product={product}
          hidden={hidden}
          blocked={buyBlocked}
          blockedMessage="معاملات فروش موقتاً توسط مدیر متوقف شده است؛ در حال حاضر امکان خرید از ما وجود ندارد."
        />
        <OrderPanel
          mode="SELL"
          product={product}
          hidden={hidden}
          blocked={sellBlocked}
          blockedMessage="خرید از مشتریان موقتاً متوقف شده است؛ در حال حاضر امکان فروش به ما وجود ندارد."
        />
      </div>

      <div className="mt-6 bg-white border border-gray-200 rounded-lg p-4 text-xs text-gray-600 leading-6 space-y-2">
        <div>✅ قیمت‌ها بر اساس قیمت خام لحظه‌ای + درصد سود مجموعه محاسبه می‌شود.</div>
        <div>✅ هر محصول دو حالت دارد: شما از ما می‌خرید یا همان مدل را با درصد منفی خرید به ما می‌فروشید.</div>
        <div>✅ پس از ثبت سفارش، کارشناسان ما برای هماهنگی تماس می‌گیرند.</div>
      </div>

      {product.priceHistories && product.priceHistories.length > 0 && (
        <div className="card p-6 mt-10">
          <h3 className="font-black text-gray-900 mb-4">آخرین تغییرات قیمت</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-right text-gray-500 border-b border-gray-200">
                  <th className="py-2">تاریخ</th>
                  <th className="py-2">قیمت خام</th>
                  <th className="py-2">درصد سود</th>
                  <th className="py-2">قیمت فروش</th>
                  <th className="py-2">قیمت خرید</th>
                  <th className="py-2">منبع</th>
                </tr>
              </thead>
              <tbody>
                {product.priceHistories.slice(0, 10).map((h) => (
                  <tr key={h.id} className="border-b border-gray-50">
                    <td className="py-2">{formatDateFa(h.createdAt)}</td>
                    <td className="py-2">{hidden ? MASKED_PRICE : formatToman(h.basePrice)}</td>
                    <td className="py-2">{toFaDigits(h.markup)}٪</td>
                    <td className="py-2 text-primary-700 font-semibold">{hidden ? MASKED_PRICE : formatToman(h.sellPrice)}</td>
                    <td className="py-2">{hidden ? MASKED_PRICE : formatToman(h.buyPrice)}</td>
                    <td className="py-2 text-gray-500">{h.source}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProductDetail
